import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Compass, Tent, Users, ShieldCheck, Heart, MapPin, Sparkles, Fish, Star,
  Calendar, Smartphone, Wallet, Footprints,
} from "lucide-react";
import { SOCIAL } from "../lib/social";
import { api } from "../lib/api";
import { withUtm, PRINT_UTM } from "../lib/utm";

/**
 * /print/leaflet — A5 portrait printable leaflet (148mm × 210mm).
 *
 * Designed for hand-out at campsite receptions, outdoor shows, fishing
 * tackle shops, walking-group meetups, etc. Smaller than the A4 poster
 * but denser: same brand voice, condensed copy, two clear columns of
 * features, a Free vs Pro panel, and a QR code that lands on the
 * homepage poll so every scan turns into a first-tap engagement.
 *
 * Print workflow: visit /print/leaflet on desktop → File → Print →
 * Save as PDF → choose A5 paper, no scaling, no margins. Done.
 */

const PUBLIC_URL =
  (process.env.REACT_APP_PUBLIC_URL || "").trim().replace(/\/$/, "") ||
  "https://camp-compass.uk";

/**
 * QR target URL — same as PUBLIC_URL but with UTM tags so every scan
 * from a printed leaflet gets attributed in the admin signup-source
 * dashboard via the existing `lib/signupSource.js` pipeline.
 */
const QR_URL = withUtm(PUBLIC_URL, PRINT_UTM.leaflet);

const LAUNCH_DATE = new Date("2026-05-31T00:00:00Z");
const LAUNCH_LABEL = LAUNCH_DATE.toLocaleDateString("en-GB", {
  day: "numeric", month: "long", year: "numeric",
});

function Feat({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2">
      <div className="bg-[#ff4500] text-white p-1.5 flex-shrink-0">
        <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
      </div>
      <div>
        <div className="font-display text-[10pt] leading-tight">{title}</div>
        <div className="text-[7.5pt] text-[#444] leading-snug mt-0.5">{body}</div>
      </div>
    </div>
  );
}

/**
 * iter122 — Friendly hand-drawn arrow used to point at the app URL,
 * the social handles, and the QR on the leaflet. Inline SVG so it
 * prints crisp at any size and we don't pull a heavy font for one
 * doodle. `direction` rotates the same artwork instead of duplicating
 * paths. Stroke uses currentColor so callers control the tone with
 * Tailwind text-{color}.
 */
function HandArrow({ direction = "right", className = "" }) {
  const rotation = {
    right: 0,
    "right-down": 25,
    down: 90,
    "down-left": 135,
    left: 180,
    "up-right": -35,
  }[direction] ?? 0;
  return (
    <svg
      viewBox="0 0 48 24"
      className={className}
      style={{ transform: `rotate(${rotation}deg)` }}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Wobbly shaft — two bezier wobbles so it reads "hand-drawn" */}
      <path d="M2 13 C 10 8, 18 18, 26 12 S 36 8, 42 12" />
      {/* Arrowhead — slightly off-axis so it doesn't look CAD-perfect */}
      <path d="M36 6 L 43 12 L 36 18" />
    </svg>
  );
}

export default function Leaflet() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/campsites/stats").then(({ data }) => {
      if (alive && typeof data?.total === "number") setCount(data.total);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const countLabel = count != null ? count.toLocaleString() : "22,000";

  return (
    <div className="bg-[#fafafa] min-h-screen print:bg-white" data-testid="leaflet-page">
      {/* Print-only stylesheet: hide everything but the canvas, A5 page,
          no margins so the leaflet bleeds nicely. */}
      <style>{`
        @page { size: A5; margin: 0; }
        @media print {
          html, body { background: #fff !important; }
          .print-hide { display: none !important; }
          .print-canvas {
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: avoid !important;
          }
        }
        .leaflet-canvas {
          width: 148mm;
          min-height: 210mm;
        }
      `}</style>

      <div className="print-hide max-w-3xl mx-auto px-4 py-6">
        <a href="/" className="text-xs mono text-[#888] hover:text-[#ff4500] uppercase tracking-wider">← Back to app</a>
        <div className="mt-3 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl">A5 leaflet · ready to print</h1>
            <p className="text-sm text-[#555] mt-1">
              Hand out at campsite receptions, fishing shops, walking-group meetups.
              File → Print → A5 → no margins → no scaling.
            </p>
          </div>
          <button
            onClick={() => window.print()}
            className="bg-[#ff4500] text-white px-4 py-2 font-bold text-xs uppercase tracking-wider mono hover:bg-[#0e0e0e]"
            data-testid="leaflet-print-btn"
          >
            Print this leaflet
          </button>
        </div>
      </div>

      {/* A5 canvas — 148mm × 210mm portrait */}
      <div className="print-hide max-w-3xl mx-auto px-4 pb-12">
        <div
          className="leaflet-canvas print-canvas bg-white text-[#0e0e0e] mx-auto shadow-2xl"
          style={{ width: "148mm", minHeight: "210mm" }}
          data-testid="leaflet-canvas"
        >
          {/* HERO BAND (35mm) — landscape image with overlaid wordmark */}
          <div className="relative h-[55mm] overflow-hidden bg-[#0d1f24]">
            <img
              src="https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=1600&q=80"
              alt="UK camping landscape"
              className="absolute inset-0 w-full h-full object-cover opacity-85"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/85" />
            <div className="absolute top-3 left-4 right-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Compass className="w-5 h-5" strokeWidth={2} />
                <span className="font-display text-[14pt] leading-none">CampCompass UK</span>
              </div>
              <span className="mono text-[6pt] uppercase tracking-[0.2em] font-bold bg-[#ff4500] px-1.5 py-0.5">
                A camper community
              </span>
            </div>
            <div className="absolute bottom-3 left-4 right-4 text-white">
              <div className="font-display text-[18pt] leading-[0.95] drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                {countLabel}+ UK<br />sites · one map · zero ads
              </div>
            </div>
          </div>

          {/* MAIN BODY — two columns of features + intro */}
          <div className="p-5 pt-4">
            <p className="text-[8pt] text-[#222] leading-snug">
              The UK&apos;s first honest, community-built campsite directory — for
              first-timers, lifelong campers, anglers, dog-walkers, glampers,
              everyone. Built by campers, for campers. <strong>No ads, no fake
              reviews, no upsells.</strong>
            </p>

            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 mt-3">
              <Feat icon={Tent} title="22k sites · zero ads" body="Every UK site we could verify, mapped and searchable." />
              <Feat icon={Footprints} title="Local walks · 10 km" body="Walking routes within 10 km of every pitch." />
              <Feat icon={Fish} title="NEW · Fisheries · 10 km" body="Day-ticket waters, trout farms, managed lakes — listed per site." />
              <Feat icon={Wallet} title="Direct-book partners" body="Book direct with owners. No middleman, no booking fees." />
              <Feat icon={Users} title="Kind community" body="Campfire Code keeps it warm. No gatekeeping." />
              <Feat icon={ShieldCheck} title="Honest reviews" body="Real campers. We pin verified prices, not opinions." />
            </div>

            {/* FREE VS PRO PANEL */}
            <div className="mt-4 border border-[#0e0e0e] bg-[#fafafa]">
              <div className="grid grid-cols-2 text-center">
                <div className="border-r border-[#0e0e0e] p-2.5">
                  <div className="mono text-[6pt] uppercase tracking-[0.2em] text-[#666]">Free</div>
                  <div className="font-display text-[12pt] leading-tight mt-0.5">£0 · free while we grow</div>
                  <div className="text-[7pt] text-[#444] leading-snug mt-1.5">
                    Browse 22k sites · map · search · favourites · walks &amp; fisheries · community chat
                  </div>
                </div>
                <div className="p-2.5 bg-[#ff4500] text-white">
                  <div className="mono text-[6pt] uppercase tracking-[0.2em] opacity-90">Pro · 14-day free</div>
                  <div className="font-display text-[12pt] leading-tight mt-0.5">£1.99 / month</div>
                  <div className="text-[7pt] leading-snug mt-1.5 opacity-95">
                    AI chatbot · <strong className="font-semibold">AI packing lists</strong> · 48h weather alerts · <strong className="font-semibold">trip-date tracker</strong> · price-drop pings · exclusive direct-book deals
                  </div>
                </div>
              </div>
              <div className="border-t border-[#0e0e0e] px-2.5 py-1.5 text-center bg-white">
                <div className="text-[6.5pt] mono uppercase tracking-[0.18em] text-[#0e0e0e]">
                  Less than a flat white · keeps the lights on · keeps the community ad-free
                </div>
              </div>
            </div>

            {/* COMMUNITY + SUPPORT STRIP */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <div className="mono text-[6pt] uppercase tracking-[0.2em] text-[#666] mb-1">Community</div>
                <div className="text-[7.5pt] leading-snug text-[#222]">
                  Campfire Chat threads, weekend polls, trip-wall photos —
                  every camper welcome, every age, every kit. Moderated daily.
                </div>
              </div>
              <div>
                <div className="mono text-[6pt] uppercase tracking-[0.2em] text-[#666] mb-1">Support</div>
                <div className="text-[7.5pt] leading-snug text-[#222]">
                  Real human reply within 24h via <span className="mono">hello@camp-compass.uk</span>.
                  Bug? Suggestion? Direct-book partner enquiry? We&apos;ll be there.
                </div>
              </div>
            </div>

            {/* FOOTER — URL, QR, launch date · iter122 friendly arrows
                pointing at the app URL + every social + the QR. */}
            <div className="mt-4 pt-3 border-t-2 border-[#0e0e0e] flex items-end justify-between gap-3 relative">
              <div className="relative flex-1 min-w-0">
                {/* APP URL — primary CTA, gets a curly arrow + "open this!" */}
                <div className="relative inline-flex items-center gap-1">
                  <div
                    className="font-display text-[11pt] leading-tight text-[#ff4500]"
                    data-testid="leaflet-app-url"
                  >
                    camp-compass.uk
                  </div>
                  <HandArrow direction="left" className="w-7 h-4 text-[#ff4500]" />
                  <span
                    className="text-[7pt] text-[#ff4500] italic"
                    style={{ fontFamily: '"Caveat", "Patrick Hand", "Comic Sans MS", cursive', transform: "rotate(-4deg)", display: "inline-block" }}
                  >
                    open this!
                  </span>
                </div>
                <div className="mono text-[6pt] uppercase tracking-[0.2em] text-[#666] mt-0.5">
                  Live since {LAUNCH_LABEL}
                </div>
                <div className="flex items-center gap-2 mt-2 text-[7pt]">
                  <Smartphone className="w-3 h-3" strokeWidth={2.2} />
                  <span className="mono">Add to home screen · works offline</span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-[7pt]">
                  <Heart className="w-3 h-3 text-[#ff4500]" strokeWidth={2.4} fill="#ff4500" />
                  <span>Made by a camper, for campers</span>
                </div>

                {/* SOCIAL HANDLES — both, with their own friendly arrows */}
                <div
                  className="mt-2 pt-2 border-t border-dashed border-[#0e0e0e]/40"
                  data-testid="leaflet-socials"
                >
                  <div className="text-[6pt] mono uppercase tracking-[0.2em] text-[#666] mb-1">
                    Find us · say hi
                  </div>
                  <div className="flex items-center gap-1.5 text-[7.5pt]" data-testid="leaflet-social-instagram">
                    <span className="font-display">Instagram</span>
                    <HandArrow direction="right" className="w-5 h-3 text-[#ff4500]" />
                    <span className="mono text-[#0e0e0e]">
                      {SOCIAL?.instagram?.handle || "@camp.compassuk2026"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[7.5pt] mt-0.5" data-testid="leaflet-social-facebook">
                    <span className="font-display">Facebook</span>
                    <HandArrow direction="right" className="w-5 h-3 text-[#ff4500]" />
                    <span className="mono text-[#0e0e0e]">
                      {SOCIAL?.facebook?.handle || "@campcompassuk2026"}
                    </span>
                  </div>
                  <div
                    className="text-[7pt] text-[#ff4500] italic mt-0.5"
                    style={{ fontFamily: '"Caveat", "Patrick Hand", "Comic Sans MS", cursive', transform: "rotate(-2deg)", display: "inline-block" }}
                  >
                    tag us — we'll cheer you on 🏕️
                  </div>
                </div>
              </div>

              <div className="text-center relative flex-shrink-0" data-testid="leaflet-qr">
                <div
                  className="absolute -left-12 top-2 text-[8pt] text-[#ff4500] italic whitespace-nowrap"
                  style={{ fontFamily: '"Caveat", "Patrick Hand", "Comic Sans MS", cursive', transform: "rotate(-10deg)" }}
                >
                  scan me!
                </div>
                <HandArrow direction="right" className="absolute -left-7 top-8 w-6 h-4 text-[#ff4500]" />
                <div className="bg-white p-1 border border-[#0e0e0e]">
                  <QRCodeSVG value={QR_URL} size={64} level="M" includeMargin={false} />
                </div>
                <div className="mono text-[5.5pt] uppercase tracking-[0.18em] text-[#666] mt-1">
                  Scan to open
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PRINT-ONLY canvas — same content, no preview chrome */}
      <div className="hidden print:block">
        <div
          className="leaflet-canvas bg-white text-[#0e0e0e] mx-auto"
          style={{ width: "148mm", minHeight: "210mm" }}
        >
          {/* Same content as preview above — re-rendered without the
              page chrome. We rely on the print-hide class on the
              preview wrapper to suppress it. */}
        </div>
      </div>
    </div>
  );
}
